<template>
  <BaseModal
    class="eventmap-facility-modal"
    :title="modalTitle"
    ref="modal"
    height="tall"
    startOpen
  >
    <div class="base-modal__body-row">
      <p v-if="eventmapFacility.description" class="eventmap-facility-modal__description">
        {{ eventmapFacility.description }}
      </p>
      <template v-if="Object.keys(exclusiveEventsByDay).length">
        <template v-for="(exclusiveEvents, day) in exclusiveEventsByDay" :key="day">
          <div class="eventmap-facility-modal__day-section">
            <h4 class="eventmap-facility-modal__day-title">{{ day }}</h4>
            <div
              v-for="exclusiveEvent in exclusiveEvents"
              :key="exclusiveEvent.id"
              class="eventmap-facility-modal__section"
            >
              <div class="eventmap-facility-modal__header">
                <div>
                  <div class="eventmap-facility-modal__session-title">
                    {{ exclusiveEvent.title }}
                  </div>
                  <div class="eventmap-facility-modal__time">
                    <i class="far fa-clock" style="margin-right: var(--sizing-1)"></i> {{ exclusiveEvent.formatted_start_time }} - {{ exclusiveEvent.formatted_end_time }}
                    <div class="eventmap-facility-modal__exclusive-label">
                      <div class="eventmap-facility-modal__exclusive-indicator"></div>
                      Exclusive
                    </div>
                  </div>
                </div>
                <div v-if="exclusiveEvent.policy">
                  <button
                    v-if="exclusiveEvent.policy.is_booked_on"
                    class="button button--icon-circle"
                    @click="handleBooking(exclusiveEvent.id, 'unbook')"
                  >
                    <i class="fa-regular fa-calendar-circle-minus"></i>
                  </button>

                  <button
                    v-else-if="exclusiveEvent.policy.is_available && exclusiveEvent.policy.can_book"
                    class="button button--icon-circle"
                    @click="handleBooking(exclusiveEvent.id, 'book')"
                  >
                    <i class="fa-regular fa-calendar-circle-plus"></i>
                  </button>

                  <button
                    v-else-if="!exclusiveEvent.policy.is_available"
                    class="button button--icon-circle button--disabled"
                    type="button"
                    disabled
                  >
                    <i class="fa-regular fa-calendar-circle-plus"></i>
                  </button>
                </div>
              </div>
              <div class="eventmap-facility-modal__session-description">
                <text-clamp :max-lines="2" :text="exclusiveEvent.description" />
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </BaseModal>
</template>

<script>
import axios from 'axios';
import TextClamp from 'vue3-text-clamp';
import { store } from '../../../Frontend/store';

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      eventmapFacility: {},
      exclusiveEventsByDay: {},
      loading: true,
      errored: false,
    };
  },
  components: { TextClamp },
  computed: {
    eventEdition() {
      return store.eventEdition;
    },
    modalTitle() {
      return this.loading || !this.eventmapFacility?.name ? '...' : this.eventmapFacility.name;
    },
    endpoint() {
      if (window.location.pathname.includes('/iframe/eventmap/')) {
        return `/${this.eventEdition.slug}/iframe/eventmap/facility/${this.id}`;
      }
      return `/${this.eventEdition.slug}/eventmap-facility/${this.id}`;
    },
  },
  mounted() {
    this.fetchEventmapFacility();
  },
  methods: {
    fetchEventmapFacility() {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
      axios
        .get(this.endpoint, config)
        .then((response) => {
          this.eventmapFacility = response.data.data;
          this.updateByDay();
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(() => {
          this.errored = true;
        });
    },
    updateByDay() {
      this.exclusiveEventsByDay = this.eventmapFacility.exclusive_events.reduce((acc, exclusiveEvent) => {
        const date = exclusiveEvent.formatted_start_date;
        if (!acc[date]) acc[date] = [];
        acc[date].push(exclusiveEvent);
        return acc;
      }, {});
    },
    handleBooking(exclusiveEventId, action) {
      this.loading = true;
      const url = `/${this.eventEdition.slug}/exclusive-events/${exclusiveEventId}/${action}`;
      axios
        .post(url)
        .then(() => {
          this.fetchEventmapFacility();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    modalClose() {
      this.$refs.modal.close();
    },
  },
};
</script>

<style scoped>
.eventmap-facility-modal__description {
  font-size: var(--font-small);
  color: var(--ui-grey-400);
  margin-bottom: var(--sizing-6);
}

.eventmap-facility-modal__day-section {
  margin-bottom: var(--sizing-4);
}

.eventmap-facility-modal__day-title {
  margin-bottom: var(--sizing-4);
}

.eventmap-facility-modal__section {
  margin-bottom:  var(--sizing-3);
  border-top: 1px solid var(--ui-grey-200);
  padding-top: var(--sizing-3);
  padding-bottom: var(--sizing-3);
}

.eventmap-facility-modal__header {
  display: grid;
  grid-template-columns: 1fr 40px;
}

.eventmap-facility-modal__time {
  font-size: var(--font-small);
  color: var(--ui-grey-600);
  display: flex;
  align-items: center;
}

.eventmap-facility-modal__session-title {
  font-weight: var(--font-semi-bold);
}

.eventmap-facility-modal__session-description {
  font-size: var(--font-small);
  font-weight: var(--font-light);
  color: var(--ui-grey-400);
}

.eventmap-facility-modal__exclusive-label {
  color: var(--success-color);
  display: flex;
  align-items: center;
  line-height: 1;
  padding-left: var(--sizing-2);
  margin-left: var(--sizing-3);
}

.eventmap-facility-modal__exclusive-indicator {
  background-color: var(--success-color);
  width: var(--sizing-2);
  height: var(--sizing-2);
  border-radius: var(--radius-full);
  box-shadow: 0 0 0 var(--sizing-1) color-mix(in srgb, var(--success-color) 20%, transparent);
  margin-right: var(--sizing-2);
}
</style>
